/**
 * Lineage walker for reports — Workflow Prompt 5.
 *
 * A "lineage" is a chain of reports linked by `parentReportId`. The lineage's
 * root is the report whose ancestors all have null `parentReportId`. When the
 * user picks "Add another report alongside", a fresh lineage is created (no
 * parent link), so a single report TYPE can host multiple lineages — that's
 * what this module disentangles.
 *
 * Pure / synchronous / no React — easy to unit-test.
 */
import type { SiteRagReport } from '../types/siteRag';

export interface ReportLineage {
  /** ID of the lineage's root report (ancestor with no parentReportId). */
  lineageId: string;
  /** The most-recently-created report on this lineage. */
  latest: SiteRagReport;
  /** Older versions on this lineage, sorted newest-first. Empty when the lineage is just `latest`. */
  history: SiteRagReport[];
}

/**
 * Walks `parentReportId` backwards from `start` to its root. Returns the root's
 * id. Cycle-safe (defensive — if a malformed report references itself or a
 * cycle, returns the first node in the cycle).
 */
export function findLineageRoot(start: SiteRagReport, byId: Map<string, SiteRagReport>): string {
  let cur: SiteRagReport = start;
  const seen = new Set<string>();
  while (cur.parentReportId && byId.has(cur.parentReportId) && !seen.has(cur.id)) {
    seen.add(cur.id);
    const parent = byId.get(cur.parentReportId);
    if (!parent) break;
    cur = parent;
  }
  return cur.id;
}

/**
 * Groups `reports` by lineage. Returns one ReportLineage per chain, sorted by
 * the lineage's latest createdAt (newest lineage first). Within each lineage,
 * the latest is the freshest report and history holds the rest in newest-first
 * order.
 */
export function groupReportsByLineage(reports: SiteRagReport[]): ReportLineage[] {
  const byId = new Map(reports.map(r => [r.id, r]));
  const groups = new Map<string, SiteRagReport[]>();
  for (const r of reports) {
    const rootId = findLineageRoot(r, byId);
    const existing = groups.get(rootId);
    if (existing) existing.push(r);
    else groups.set(rootId, [r]);
  }

  const lineages: ReportLineage[] = [];
  for (const [lineageId, group] of groups) {
    const sorted = [...group].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    lineages.push({
      lineageId,
      latest: sorted[0],
      history: sorted.slice(1),
    });
  }

  // Sort lineages by their latest createdAt — most recent activity first.
  return lineages.sort(
    (a, b) => new Date(b.latest.createdAt).getTime() - new Date(a.latest.createdAt).getTime(),
  );
}

/** Distinct report types on a site — revision versions of the same type count once. */
export function countDistinctReportTypes(reports: readonly SiteRagReport[]): number {
  if (reports.length === 0) return 0;
  const types = new Set<string>();
  for (const r of reports) types.add(r.reportType ?? 'feasibility');
  return types.size;
}

/** User-facing label, e.g. "3 reports" or "1 report". */
export function formatReportTypeCountLabel(count: number): string {
  return `${count} report${count === 1 ? '' : 's'}`;
}
